import { useEffect, useRef } from 'react';
import { AccessibilityInfo } from 'react-native';
import { useTranslation } from 'react-i18next';
import { formatSlotDuration } from '@heraion/shared';

import { isPrescribedBoutSet } from './deriveEntryKind';
import { resolveRowIndex, type DraftEntry } from './state/loggerDraftTypes';
import type { UseHoldTimer } from './useHoldTimer';

// HI-1 slice 2 — the bout that closes itself (interval-training.md §9.3).
//
// A prescribed `countdown` set is bounded in time: at zero the set is DONE,
// and the athlete should not have to tap to say so. Eight bouts of 30/90 is
// eight taps the clock already knows about, on a gesture that is affordable
// once and hostile eight times ([[feedback_work_by_default_no_useless_taps]]).
//
// ⭐ What this hook does NOT do is as important as what it does: it does not
// write anything, does not touch the reducer, and does not complete a set on
// its own. It calls exactly the callback the athlete's own tap calls —
// onLogSet(undefined, seconds) — so the whole downstream chain (update_set
// with the duration, the PR check, the rest armed from THIS set's prescription,
// the cursor handed to the next bout by useRestAdvance) is the same one road
// every other completion travels. A second completion path is how a timed set
// ends up written twice, or written differently ([[feedback_wire_the_create_path_not_just_read]]).
//
// The reason nothing here schedules the NEXT bout: it does not have to.
// commitSet already arms the rest, and useRestAdvance.apply() is already "the
// ONLY place the cursor moves after a log". Auto-advance was never missing —
// what was missing is a bout that ends.
export function useWorkBoutAutoComplete({
  hold,
  entries,
  activeEntryIdx,
  boundSetIdx,
  onLogSet,
}: {
  hold: UseHoldTimer;
  entries: DraftEntry[];
  activeEntryIdx: number;
  boundSetIdx: number;
  /** The logger's own completion leg. Same signature the effort-meter tap
   *  uses; `undefined` for rir is a real value here (a bout can end without
   *  the athlete declaring an effort) and onLogSet already accepts it. */
  onLogSet: (rir?: number, durationSec?: number) => unknown;
}): void {
  // Which target we have already fired for, as "entryIdx:setIdx". Without it
  // the effect re-fires on every render that keeps atZero true. capture()
  // clears the timer on the first pass, but a re-entrant second pass is a
  // second onLogSet against the NEXT bound set, which would complete a set
  // nobody has done yet.
  const firedFor = useRef<string | null>(null);
  const { t } = useTranslation();

  const target = hold.target;
  const { atZero, mode } = hold.timer.state;

  useEffect(() => {
    if (!target) {
      firedFor.current = null;
      return;
    }
    const key = `${target.entryId}:${target.setIdx}`;
    if (firedFor.current === key) return;
    // Only a prescribed countdown bout ends by itself. A hold (count-up) and
    // a countdown the athlete started by hand are MEASURING: they run until
    // the athlete says stop, and turning them off at zero would cap the very
    // number they exist to capture (TM-13).
    // LG-43 — where that row is NOW, not where it was when the bout started.
    const timedEntryIdx = resolveRowIndex(entries, target.entryId);
    if (!isPrescribedBoutSet(entries[timedEntryIdx]?.sets[target.setIdx]))
      return;
    if (mode !== 'down') return;
    // The engine owns what "zero" means (useCountTimer's atZero): a paused
    // clock sitting on the deadline is not a finished one, and nothing armed
    // is not zero either.
    if (!atZero) return;
    // ⚠️ The gate, and it is the reason this hook takes the bound indices
    // instead of trusting `target`: onLogSet completes the BOUND set, not the
    // timed one. Walk away mid-bout and the two are different sets — firing
    // here would hand the next exercise's set the seconds of a bout it never
    // did. capture() enforces the same rule from its side (it returns null and
    // touches nothing when the running timer belongs elsewhere), so the two
    // agree by construction rather than by both remembering the same thing.
    const seconds = hold.capture(activeEntryIdx, boundSetIdx);
    if (seconds === null) return;
    firedFor.current = key;
    onLogSet(undefined, seconds);
    // ⭐ The set changed state and NOBODY TOUCHED THE PHONE, so nothing else
    // announces it. LoggerRestBar's rest-start announcement covers part of this
    // by accident — but only when a rest is actually armed, and a prescribed
    // restSec of 0 (a real prescription: straight into the next bout) arms
    // none, which would leave a blind athlete with no signal at all. It says
    // the seconds because that is the fact worth hearing: the bout ran its
    // length. The audible half — a cue that distinguishes work-starts from
    // rest-starts, and the 3-2-1 before each — is slice 2b.
    AccessibilityInfo.announceForAccessibility(
      t('mobile.logger.boutAutoLoggedA11y', {
        time: formatSlotDuration(seconds),
        defaultValue: 'Bout done, {{time}} logged.',
      }),
    );
  }, [
    target,
    atZero,
    mode,
    entries,
    activeEntryIdx,
    boundSetIdx,
    hold,
    onLogSet,
    t,
  ]);
}
